import ApiService from '@/services/ApiService';
import { defineStore } from 'pinia';
import { useIdentityStore } from '@/store/IdentityStore';
import { useSegmentStore } from '@/store/SegmentStore';
import { useSettingsStore } from '@/store/SettingsStore';
import { useSprinklerStore } from '@/store/SprinklerStore';
import { useModalStore } from './ModalStore';

export const useInputStore = defineStore('input', {
  state () {
    return {
      validInputs: true,
      xAxisLabelFrequency: 3,
      marginValueCount: 8,
      xAxisGap: 20,
      sliderGap: 40,
      sprinklerSpacing: 10,
      pipeLeftOffset: 20,
      pipeRightOffset: 20,
      pipeEditorWidth: 0,
      offsetSide: null,
      chartAxisLabelWidth: 0,
      leftOffsetBracketWidth: 0,
      rightOffsetBracketWidth: 0,
      pressureFlowChartHeight: 0,
      pressureFlowChartWidth: 0,
      hasCalcData: false,
      calcData: {
        totalFlow: 0,
        inletPressure: 0,
        inletLocation: 0,
        legs: [
          {
            segments: [
              {
                pipeId: 0,
                nozzleId: 0,
                length: 0,
                slope: 0,
                sprinklerPressure: 0,
                velocity: 0,
              }
            ],
            outletFlow: 0,
            outletPressure: 0,
            outletVelocity: 0,
            flushTime: 0
          }
        ],
        pressureVariation: 0,
        pressureDifference: 0,
        appRate: 0,
        flowVariation: 0,
        flowDifference: 0,
        averageNozFlow: 0,
        maxNozFlow: 0,
        minNozFlow: 0,
        segments: [],
      },
      formattedPipeData: {},
      calculatedSegments: [],
      inputData: [
        {
          label: 'Job Description',
          id: 'jobDescription',
          placeholder: 'Job Description',
          value: 'Test Description',
        },
        {
          label: 'Job Number',
          id: 'jobNumber',
          placeholder: 'Job Number',
          value: 'Test Job Number',
        },
        {
          label: 'Client',
          id: 'clientName',
          placeholder: 'Client Name',
          value: 'Test Client',
        },
        {
          label: 'Client Number',
          id: 'clientNumber',
          placeholder: 'Client Number',
          value: 'Test Client Number',
        },
      ],
      offsets: {
        initialMagScale: 1,
        left: {
          lockValue: null,
          displayValue: 0,
          ref: null
        },
        right: {
          lockValue: null,
          displayValue: 0,
          ref: null
        }
      },
      jobData: {
        jobNumber: 'Enter Job'
      },
      spacingExpanded: true,
      segments: [],
      debugLog: {
        segments: {},
        valueToApi: {},
        apiReturn: {},
      }
    };
  },
  getters: {
    getSpacingExpanded () {
      return this.spacingExpanded;
    },
    getPipeLeftOffset () {
      return this.pipeLeftOffset;
    },
    getPipeRightOffset () {
      return this.pipeRightOffset;
    },
    getChartAxisLabelWidth () {
      return this.chartAxisLabelWidth;
    },
    getLeftOffsetBracketWidth () {
      return this.leftOffsetBracketWidth;
    },
    getPressureFlowChartHeight () {
      return this.pressureFlowChartHeight;
    },
    getPressureFlowChartWidth () {
      return this.pressureFlowChartWidth;
    },
    getRightOffsetBracketWidth () {
      return this.rightOffsetBracketWidth;
    },
    getSprinklerSpacing () {
      return this.sprinklerSpacing;
    },
    getInputData () {
      return this.inputData;
    },
    getMarginValueCount () {
      return this.marginValueCount;
    },
    getHasCalcData () {
      return this.hasCalcData;
    },
    getOffsets () {
      return this.offsets;
    },
    getLeftOffset () {
      return this.offsets.left.displayValue;
    },
    getRightOffset () {
      return this.offsets.right.displayValue;
    },
    getXAxisGap () {
      return this.xAxisGap;
    },
    getXAxisLabelFrequency () {
      return this.xAxisLabelFrequency;
    },
    getCalcData () {
      return this.calcData;
    },
    getFormattedPipeData () {
      return this.formattedPipeData;
    },
    getSegments () {
      return this.calculatedSegments;
    },
    getSliderGap () {
      return this.sliderGap;
    },
    getCurrentSegments () {
      return this.segments;
    },
    getChartPointsHeight () {
      // 28 is radius of offset
      // TODO set radius offset in store
      return this.getPressureFlowChartHeight - this.getSliderGap -  useSettingsStore().getGraphTopChartSpacing;
    },
    getJobNumber () {
      return this.jobData.jobNumber;
    },
    getJobDescription () {
      return 'placeholder job description';
    },
    getDebugLog () {
      return this.debugLog;
    },
    getPipeEditorWidth () {
      let magRight = this.getOffsets.right.lockValue  / this.getOffsets.initialMagScale;
      let magLeft = this.getOffsets.left.lockValue / this.getOffsets.initialMagScale;
      if (this.offsetSide === 'right') {
        return this.pipeEditorWidth - this.getRightOffset - this.getLeftOffset - (this.getXAxisGap * 2);
      } else if (this.offsetSide === 'left') {
        return this.pipeEditorWidth - this.getLeftOffset - this.getRightOffset - (this.getXAxisGap * 2);
      } else {
        return this.pipeEditorWidth - magLeft - magRight - (this.getXAxisGap * 2);
      }
    },
  },
  actions: {
    setFormattedPipeData (data) {
      this.formattedPipeData = data;
    },
    setJobNumber (value) {
      this.jobData.jobNumber = value;
    },
    setPipeEditorWidth (num) {
      this.pipeEditorWidth = num;
    },
    toggleSpacingExpanded () {
      this.spacingExpanded = !this.spacingExpanded;
    },
    setPipeLeftOffset (value) {
      this.pipeLeftOffset = value;
    },
    setPipeRightOffset (value) {
      this.pipeRightOffset = value;
    },
    restoreSegments () {
      const segmentInfoString = useIdentityStore().User.properties.rotator_Segments;
      const segmentStore = useSegmentStore();
      if (segmentInfoString == undefined) {
        if (segmentStore.numberOfSegmentsOfType('pipe') == 0) { segmentStore.addDefaultSegmentForType('pipe'); }
        return;
      }
      let storedSegments = JSON.parse(segmentInfoString);
      if (storedSegments.pipeSegments == undefined) { storedSegments = JSON.parse('{ "Segments": ' + storedSegments + ' }').Segments; }
      this.offsets.left.displayValue = storedSegments.leftOffset;
      this.offsets.right.displayValue = storedSegments.rightOffset;
      this.sprinklerSpacing = storedSegments.sprinklerSpacing;
      segmentStore.restoreSegments(storedSegments);
      this.sprinklerCalc(false);
    },
    handleAddSegment (type) {
      const segmentStore = useSegmentStore();
      segmentStore.addDefaultSegmentForType(type);
      segmentStore.validateSlopeMinimumLength();
      this.sprinklerCalc();
    },
    async sprinklerCalc (shouldUpdateUserProperty = true) {
      const segmentStore = useSegmentStore();
      this.segments = [];
      let pipeTotalLength = segmentStore.getAllSegmentLengths('pipe');
      let offsetL = this.getLeftOffset;
      let offsetR = this.getRightOffset;
      let offsetPipeTotalLength = 0;
      let offset = 0;
      // // offset cases
      if (segmentStore.getInletPosition === 0) {
        // inlet all the way to the left
        offset = offsetL;
        if (offsetL > 0 && offsetR === 0 || offsetL === 0 && offsetR === 0) {
          // offset left or offset none
          offsetPipeTotalLength = pipeTotalLength;
        } else if (offsetL === 0 && offsetR > 0 || offsetL > 0 && offsetR > 0) {
          // offset right or offset both
          offsetPipeTotalLength = pipeTotalLength - offsetR;          
        } 
        // adds offset pipe if left offset
        if (offsetL != 0) {
          this.segments.push({
            xPosition: 0,
            pipeId: null,
            nozzleId: null,
            slope: 0
          });
        }        
      } else if (segmentStore.getInletPosition === pipeTotalLength) {
        offset = offsetL;
        if (offsetL === 0 && offsetR > 0) {
          // offset right or offset none
          offsetPipeTotalLength = pipeTotalLength - offsetR;
        } else if (offsetL > 0 && offsetR === 0) {
          // offset left or offset both
          offsetPipeTotalLength = pipeTotalLength;
        } else if ( offsetL > 0 && offsetR > 0) {
          offsetPipeTotalLength = pipeTotalLength - offsetR;
        } else if (offsetL === 0 && offsetR === 0) {
          offsetPipeTotalLength = pipeTotalLength;
        }
        if (offsetL != 0) {
          this.segments.push({
            xPosition: 0,
            pipeId: null,
            nozzleId: null,
            slope: 0
          });
        } 
      } else if (segmentStore.getInletPosition > 0 && segmentStore.getInletPosition < pipeTotalLength) {
        offset = 0;
        if (offsetL === 0 && offsetR > 0) {
          // offset right
          offsetPipeTotalLength = pipeTotalLength - offsetR;
        } else if (offsetL > 0 && offsetR === 0) {
          offset = offsetL;
          offsetPipeTotalLength = pipeTotalLength;
        } else if (offsetL > 0 && offsetR > 0) {
          // offset both
          offsetPipeTotalLength = pipeTotalLength - offsetR;
        } else if (offsetL === 0 && offsetR === 0) {
          // offset none
          offsetPipeTotalLength = pipeTotalLength;
        }
        // adds offset pipe if left offset
        if (offsetL != 0) {
          this.segments.push({
            xPosition: 0,
            pipeId: null,
            nozzleId: null,
            slope: 0
          });
        }  
      }
      // generates segments with sprinklers
      for (let index = 0 + offset; index < offsetPipeTotalLength;) {
        this.segments.push({
          xPosition: index,
          pipeId: null,
          nozzleId: useSprinklerStore().getNozzleSelectedId,
          slope: 0
        });
        index = index + this.sprinklerSpacing;
      }
      let pipes = segmentStore.getSegments('pipe');
      let slopes = segmentStore.getSegments('slope');
      // functionality to figure out the broken segments with pipe/slope change
      let pipePosition = 0;
      pipes.forEach((element) => {
        element.type = 'pipe';
        if (pipePosition === 0) {
          element.startPosition = 0;
          element.endPosition = element.value;
        } else {
          element.startPosition = pipePosition;
          element.endPosition = element.value + pipePosition;
        }
        pipePosition = pipePosition + element.value;
      });
      let slopePosition = 0;
      slopes.forEach((element) => {
        element.type = 'slope';
        if (slopePosition === 0) {
          element.startPosition = 0;
          element.endPosition = element.value;
        } else {
          element.startPosition = slopePosition;
          element.endPosition = element.value + slopePosition;
        }
        slopePosition = slopePosition + element.value;
      });
      segmentStore.getSegments().forEach((item) => {
        if (item.type === 'pipe') {
          if (item.endPosition != pipeTotalLength) {
            this.segments.push(
              { 
                xPosition: item.endPosition,
                pipeId: null,
                nozzleId: null,
                slope: null
              }
            );
          }
        } else if (item.type === 'slope') {
          if (pipeTotalLength > item.endPosition) {
            this.segments.push(
              { 
                xPosition: item.endPosition,
                pipeId: null,
                nozzleId: null,
                slope: null 
              }
            );
          }
        }
      });
      if (offsetR != 0) {
        this.segments.push({
          xPosition: pipeTotalLength,
          pipeId: segmentStore.lastSegmentOfType('pipe').id,
          nozzleId: null,
          slope: segmentStore.lastSegmentOfType('slope').slope
        });
      } 
      this.segments.sort((a, b) => a.xPosition - b.xPosition);
      // adds pipe ids and slopes to segments 
      pipes.forEach((item) => {
        this.segments.forEach((seg) => {
          if (seg.xPosition >= item.startPosition && seg.xPosition <= item.endPosition) {
            seg.pipeId = item.id;
          }
        });
      });
      slopes.forEach((item) => {
        this.segments.forEach((seg) => {
          if (seg.xPosition >= item.startPosition && seg.xPosition <= item.endPosition) {
            seg.slope = item.slope;
          }
        });
      });
      if (shouldUpdateUserProperty) {
        const property = {
          inletPressure: segmentStore.getInletPressure,
          inletXPosition: segmentStore.getInletPosition,
          pipeSegments: segmentStore.getSegments('pipe'),
          slopeSegments: segmentStore.getSegments('slope'),
          rightIsUnclamped: segmentStore.getRightIsUnclamped,
          leftIsUnclamped: segmentStore.getLeftIsUnclamped,
          leftOffset: this.offsets.left.displayValue,
          rightOffset: this.offsets.right.displayValue,
          sprinklerSpacing: this.sprinklerSpacing,
        };
        useIdentityStore().setUserProperty({ Segments: JSON.stringify(property) }, true);
        return;
      }
      if (this.segments.length == 0) { return; }

      // creates the payload needed
      const value = {};
      value.inletPressure = segmentStore.getInletPressure;
      value.systemLength = pipeTotalLength;
      value.inletXPosition = segmentStore.getInletPosition;
      value.segments = this.segments;
      this.addSpecialSegments(value.inletXPosition, value.segments);
      value.rightLegOpen = segmentStore.getRightIsUnclamped;
      value.leftLegOpen = segmentStore.getLeftIsUnclamped;
      await ApiService.sprinklerCalc(value)
        .then((response) => {
          this.hasCalcData = true;
          this.calcData = response.data;
          this.debugLog.apiReturn = this.calcData;
          this.calculatedSegments = [];
          this.calcData.segments.forEach((seg) => { this.calculatedSegments.push(seg); });
          let a = 0;
          this.calculatedSegments.forEach((segment) => {
            segment.num = a;
            a++;
          });
          this.calculatedSegments = this.calculatedSegments.filter((item) => item.nozzleId != null);
          if (this.calcData.pressureDifference > 2000000) {
            this.validInputs = false;
            useModalStore().showModal('excessive-pressure-drop');
          } else {
            this.validInputs = true;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    addSpecialSegments (inletPosition, segments) {
      const pipes = useSegmentStore().getSegments('pipe');
      pipes.forEach((pipe, index) => {
        if (index != 0 && (pipe.id != pipes[index - 1].id || inletPosition == pipe.startPosition)) {
          segments.push({ xPosition: pipe.startPosition, pipeId: pipe.id, slope: pipe.slope });
        }
      });
      segments.sort((a, b) => a.xPosition - b.xPosition);
    },
    setSegments (arr) {
      this.segments = arr;
    },
    setChartAxisLabelWidth (num) {
      this.chartAxisLabelWidth = num;
    },
    setLeftOffsetBracketWidth (num) {
      this.leftOffsetBracketWidth = num;
    },
    setPressureFlowChartHeight (num) {
      this.pressureFlowChartHeight = num;
    },
    setPressureFlowChartWidth (num) {
      this.pressureFlowChartWidth = num;
    },
    setRightOffsetBracketWidth (num) {
      this.rightOffsetBracketWidth = num;
    },
    setSprinklerSpacing (num) {
      this.sprinklerSpacing = num;
    },
    setLeftOffset (num) {
      this.offsets.left.displayValue = num;
    },
    setRightOffset (num) {
      this.offsets.right.displayValue = num;
    },
    getOffset (dir) {
      return dir === 'left' ? this.getLeftOffset : this.getRightOffset;
    },
    initializeOffset (dir, ref) {
      this.offsets[dir].ref = ref;
    },
    lockOffsets () {
      this.offsets.left.lockValue = this.offsets.left.displayValue;
      this.offsets.right.lockValue = this.offsets.right.displayValue;
      // todo: update variable - 40 is the xAxisGap
      this.offsets.initialMagScale = useSegmentStore().getMagScale;
    },
    unlockOffsets () {
      this.offsets.left.lockValue = null;
      this.offsets.right.lockValue = null;
    },
    mapMinMaxRange (point, type) {
      const settingsStore = useSettingsStore();
      let min = 0;
      let max = 0;
      let scaleFactor = 0;
      if (type == 'pressure') {
        min = settingsStore.unfilterPressure(settingsStore.prettyChartMin(settingsStore.filterPressure(this.getCalcData.minSprinklerPressure)));
        max = this.getCalcData.maxSprinklerPressure;
        scaleFactor = this.chartScaleFactor(true);
      } else {
        min = settingsStore.unfilterEmitterFlow(settingsStore.prettyChartMin(settingsStore.filterEmitterFlow(this.getCalcData.minNozFlow)));
        max = this.getCalcData.maxNozFlow;
        scaleFactor = this.chartScaleFactor(false);
      }
      // dividing by zero issue R2000 Brown 1.25 2000 FC
      let range = (min < 0) ? Math.abs(max) + Math.abs(min) : max - min;
      let percent = (point - min) / range;
      if (!settingsStore.getRaiseYAxisOrigin) {
        range = max;
        percent = point / range;
      }
      percent = percent * scaleFactor;
      return this.getChartPointsHeight - (percent * this.getChartPointsHeight) + settingsStore.getGraphTopChartSpacing;
    },
    chartScaleFactor (isPressure) {
      const settingsStore = useSettingsStore();
      let divisions = isPressure ? settingsStore.getPressureYAxisDivision : settingsStore.getFlowYAxisDivision;
      let max = isPressure ? settingsStore.filterPressure(this.getCalcData.maxSprinklerPressure) : settingsStore.filterEmitterFlow(this.getCalcData.maxNozFlow);
      let min = isPressure ? settingsStore.filterPressure(this.getCalcData.minSprinklerPressure) : settingsStore.filterEmitterFlow(this.getCalcData.minNozFlow);
      min = settingsStore.getRaiseYAxisOrigin ? settingsStore.prettyChartMin(min) : 0;
      var step = settingsStore.chartStep(max, min, divisions);
      return step / settingsStore.prettyChartStep(step);
    },
    getInputDataFor (label) {
      return this.inputData.find((d)=>d.label === label);
    },
    getCalcDataValue (key) {
      return this.calcData[key];
    }
  }
});